import type { HTMLAttributes, InputHTMLAttributes, SyntheticEvent } from 'react'
import { useEffect, useRef, useState } from 'react'

export type AutocompleteCloseReason = 'escape' | 'selectOption' | 'blur'

export interface UseAutocompleteProps<T> {
  options: T[]
  open?: boolean
  getOptionLabel?: (option: T) => string
  getOptionDisabled?: (option: T) => boolean
  isOptionEqualToValue?: (option: T, value: T) => boolean
  onChange?: (event: SyntheticEvent, value: T | null) => void
  onClose?: (event: SyntheticEvent, reason: AutocompleteCloseReason) => void
  filterOptions?: (options: T[], state: { inputValue: string }) => T[]
}

const defaultFilterOptions = <T>(
  options: T[],
  state: { inputValue: string },
  getOptionLabel: (option: T) => string
): T[] => {
  const input = state.inputValue.toLowerCase().trim()
  if (!input) {
    return options
  }
  return options.filter(option => getOptionLabel(option).toLowerCase().includes(input))
}

export function useAutocomplete<T>(props: UseAutocompleteProps<T>) {
  const {
    options,
    open = true,
    getOptionLabel = (option: T) => String(option),
    getOptionDisabled,
    onChange,
    onClose,
    filterOptions,
  } = props

  const [inputValue, setInputValue] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const listboxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const filtered = open
    ? filterOptions
      ? filterOptions(options, { inputValue })
      : defaultFilterOptions(options, { inputValue }, getOptionLabel)
    : []

  const clampIndex = (index: number) => {
    if (filtered.length === 0) {
      return -1
    }
    // Skip disabled options
    let attempts = 0
    let i = ((index % filtered.length) + filtered.length) % filtered.length
    while (getOptionDisabled?.(filtered[i]) && attempts < filtered.length) {
      i = (((i + 1) % filtered.length) + filtered.length) % filtered.length
      attempts++
    }
    return attempts >= filtered.length ? -1 : i
  }

  /** Scroll the highlighted option into view */
  useEffect(() => {
    if (highlightedIndex < 0 || !listboxRef.current) {
      return
    }
    const items = listboxRef.current.querySelectorAll('[role="option"]')
    items[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex])

  const getRootProps = (): HTMLAttributes<HTMLDivElement> => ({
    role: 'combobox',
    'aria-expanded': open,
  })

  const getInputProps = (): Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
    ref: React.RefObject<HTMLInputElement | null>
  } => ({
    ref: inputRef,
    value: inputValue,
    onChange: (ev: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue((ev.target as HTMLInputElement).value)
      setHighlightedIndex(-1)
    },
    onKeyDown: (ev: React.KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'ArrowDown') {
        ev.preventDefault()
        setHighlightedIndex(prev => clampIndex(prev + 1))
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault()
        setHighlightedIndex(prev => clampIndex(prev - 1))
      } else if (ev.key === 'Enter') {
        ev.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
          const option = filtered[highlightedIndex]
          if (!getOptionDisabled?.(option)) {
            onChange?.(ev as unknown as SyntheticEvent, option)
          }
        }
      } else if (ev.key === 'Escape') {
        ev.preventDefault()
        onClose?.(ev as unknown as SyntheticEvent, 'escape')
      }
    },
    'aria-autocomplete': 'list' as const,
    autoComplete: 'off',
  })

  const getListboxProps = (): HTMLAttributes<HTMLDivElement> & { ref: React.RefObject<HTMLDivElement | null> } => ({
    ref: listboxRef,
    role: 'listbox',
  })

  const getOptionProps = ({ option, index }: { option: T; index: number }): HTMLAttributes<HTMLElement> => ({
    role: 'option',
    'aria-disabled': getOptionDisabled?.(option) || undefined,
    'aria-selected': highlightedIndex === index,
    className: highlightedIndex === index ? 'Mui-focused' : undefined,
    onClick: (ev: React.MouseEvent<HTMLElement>) => {
      if (!getOptionDisabled?.(option)) {
        onChange?.(ev as unknown as SyntheticEvent, option)
      }
    },
    onMouseEnter: () => {
      setHighlightedIndex(index)
    },
  })

  return {
    getRootProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions: filtered,
    inputValue,
  }
}
